import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { fmtINR } from "../lib/api";
import { Button } from "../components/ui/button";
import { initPixel, track } from "../lib/tracking";

// SIMULATED PhonePe checkout page — active only in test mode (no live credentials).
export default function MockPay() {
  const { txnId } = useParams();
  const [params] = useSearchParams();
  const [txn, setTxn] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const navigate = useNavigate();
  const back = params.get("back") || "";

  const load = useCallback(() => {
    api.get(`/payments/${txnId}`).then((r) => setTxn(r.data)).catch(() => setTxn(false));
  }, [txnId]);
  useEffect(() => { load(); }, [load]);

  const settle = async (result) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/payments/mock/${txnId}`, { result });
      setDone(data.status);
      if (data.status === "COMPLETED") {
        toast.success("Payment successful");
        // Platform ₹299 subscription purchase — fires ONLY on confirmed success.
        const amt = txn?.amount || 299;
        api.get("/public/seo/platform").then((r) => { if (r.data.pixel_id) initPixel(r.data.pixel_id); }).catch(() => {});
        track("purchase", { value: amt, currency: "INR", order_id: txnId }, { scope: "platform" });
      }
      else toast.error("Payment failed");
    } catch {
      toast.error("Could not process payment");
    } finally {
      setBusy(false);
    }
  };

  if (txn === null) return <div className="min-h-screen flex items-center justify-center bg-[#5F259F]"><Loader2 className="h-8 w-8 animate-spin text-white" /></div>;
  if (txn === false) return <div className="min-h-screen flex items-center justify-center bg-background"><p className="text-muted-foreground">Transaction not found</p></div>;

  return (
    <div className="min-h-screen bg-[#5F259F] flex items-center justify-center p-6">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md bg-white rounded-2xl overflow-hidden shadow-2xl">
        <div className="bg-[#5F259F] px-8 py-6 text-white">
          <p className="font-display font-extrabold text-xl tracking-tight">PhonePe <span className="text-xs font-sans font-semibold bg-white/20 rounded-full px-2 py-0.5 ml-2 align-middle">TEST MODE</span></p>
          <p className="text-white/70 text-xs mt-1">Simulated checkout — no real money moves</p>
        </div>
        <div className="p-8" data-testid="mockpay-panel">
          {!done && txn.status === "PENDING" && (
            <>
              <p className="overline-label text-zinc-500">Amount payable</p>
              <p data-testid="mockpay-amount" className="font-display font-extrabold text-4xl tracking-tight text-zinc-900 mt-2">{fmtINR(txn.amount)}</p>
              <p className="text-xs text-zinc-500 mt-2">Txn: {txn.txn_id} · {txn.purpose === "invoice" ? "Partner subscription" : "Store order"}</p>
              <div className="mt-8 space-y-3">
                <Button data-testid="mockpay-success-button" disabled={busy} onClick={() => settle("success")}
                  className="w-full h-12 rounded-full bg-[#5F259F] hover:bg-[#4a1d7c] text-white font-bold active:scale-95 transition-transform">
                  {busy ? "Processing…" : "Pay with UPI (Simulate Success)"}
                </Button>
                <Button data-testid="mockpay-fail-button" disabled={busy} variant="outline" onClick={() => settle("failed")}
                  className="w-full h-12 rounded-full font-bold text-zinc-700">Simulate Failure</Button>
              </div>
            </>
          )}
          {(done || txn.status !== "PENDING") && (
            <div className="text-center py-4">
              {(done || txn.status) === "COMPLETED" ? (
                <>
                  <ShieldCheck className="h-16 w-16 text-emerald-500 mx-auto" />
                  <p data-testid="mockpay-success-message" className="font-display font-extrabold text-2xl text-zinc-900 mt-4">Payment successful</p>
                  <p className="text-sm text-zinc-500 mt-1">{fmtINR(txn.amount)} received</p>
                </>
              ) : (
                <>
                  <XCircle className="h-16 w-16 text-red-500 mx-auto" />
                  <p data-testid="mockpay-failed-message" className="font-display font-extrabold text-2xl text-zinc-900 mt-4">Payment failed</p>
                  <p className="text-sm text-zinc-500 mt-1">No amount was charged</p>
                </>
              )}
              <Button data-testid="mockpay-continue-button" onClick={() => navigate(back || (txn.purpose === "invoice" ? "/partner/billing" : "/"))}
                className="mt-8 w-full h-12 rounded-full bg-zinc-900 hover:bg-black text-white font-bold">Continue</Button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
